import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { esignatureRecipientService } from "../services/esignatureRecipientService";
import { esignatureRecipientDisplayId } from "./EsignatureRecipientCommonStore";
import { EsignatureRecipient } from "../../../api/graphql";
import { esignatureDocumentDisplayId } from "../../esignatureDocument/store/EsignatureDocumentCommonStore";
import { EsignatureRecipientStatus } from "../../../api/graphql";
import { esignatureDocumentService } from "../../esignatureDocument/services/esignatureDocumentService";
import { InitializeParams } from "../../../store/types";

export class EsignatureRecipientViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = esignatureRecipientService;
  esignatureRecipientInstance: EsignatureRecipient | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  documentService = esignatureDocumentService;
  documentSearchValue: string = "";
  documentIdValue: string = "";
  documentSelectedValue: { displayId: string; id: string } | null = null;
  emailValue: string = "";
  nameValue: string = "";
  roleValue: string = "";
  orderValue: number = 0;
  statusValue: EsignatureRecipientStatus = "" as EsignatureRecipientStatus;
  signedAtValue: Date | null = null;

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadEsignatureRecipientById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.esignatureRecipientOne({ id });
    this.esignatureRecipientInstance = result.esignatureRecipientOne;
    if (this.esignatureRecipientInstance) {
      this.loadValues(this.esignatureRecipientInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.esignatureRecipientInstance) {
      return esignatureRecipientDisplayId(
        this.esignatureRecipientInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EsignatureRecipientSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  *returnedData(params: CallbackDataParams): any {
    if (
      (!params.storeId || params.storeId === this.storeId) &&
      params.returnData &&
      !params.returnData.cancelled &&
      params.returnData.resultId
    ) {
      if (params.callerData?.fieldName === "document") {
        const api = yield* getApi(
          this.rootStore.authStore,
          this.rootStore.activityStore
        );
        const result = yield api.esignatureDocumentOne({
          id: params.returnData.resultId,
        });
        const esignatureDocument = result.esignatureDocumentOne;
        this.documentSelectedValue = esignatureDocument;
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  get documentValue() {
    if (this.documentSelectedValue) {
      return {
        id: this.documentSelectedValue.id,
        displayId: esignatureDocumentDisplayId(
          this.documentSelectedValue,
          this.langStore
        ),
      };
    }
    return null;
  }

  getStatusOptions() {
    return this.langStore.EsignatureRecipientStatusOptions;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.esignatureRecipientInstance = null;
    this.documentSearchValue = "";
    this.documentIdValue = "";
    this.emailValue = "";
    this.nameValue = "";
    this.roleValue = "";
    this.orderValue = 0;
    this.statusValue = "" as EsignatureRecipientStatus;
    this.signedAtValue = null;
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelEsignatureRecipient() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editEsignatureRecipient() {
    const id = this.esignatureRecipientInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/esignatureRecipient/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printEsignatureRecipient(handlePrint: () => void): void {
    handlePrint();
  }

  *closeEsignatureRecipient(): any {
    yield this.close();
  }

  loadValues(instance: EsignatureRecipient) {
    this.idValue = instance.id;
    this.documentSelectedValue = instance.document;
    this.emailValue = instance.email;
    this.nameValue = instance.name;
    this.roleValue = instance.role;
    this.orderValue = instance.order;
    this.statusValue = instance.status;
    this.signedAtValue = instance.signedAt ? new Date(instance.signedAt) : null;
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadEsignatureRecipientById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
